import { useMemo, useState } from 'react'
import { copyTableForExcel, parseAiAnswerSegments } from '@/lib/aiAnswerTables'

function AiAnswerTable({ rows }) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await copyTableForExcel(rows)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  if (!rows?.length) return null
  const [header, ...body] = rows

  return (
    <div className="my-3 overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <p className="text-xs text-brand-muted">
          Table ready for Excel — use Copy, then paste into a worksheet.
        </p>
        <button
          type="button"
          onClick={handleCopy}
          className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium hover:bg-slate-50"
        >
          {copied ? 'Copied' : 'Copy for Excel'}
        </button>
      </div>
      <div className="max-h-[28rem] overflow-auto">
        <table className="w-full min-w-[32rem] border-collapse text-left text-xs">
          <thead className="sticky top-0 bg-slate-100">
            <tr>
              {header.map((cell, index) => (
                <th
                  key={`h-${index}`}
                  className="border-b border-slate-200 px-3 py-2 font-medium whitespace-nowrap"
                >
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, rowIndex) => (
              <tr key={`r-${rowIndex}`} className="odd:bg-slate-50/80">
                {row.map((cell, cellIndex) => (
                  <td
                    key={`c-${rowIndex}-${cellIndex}`}
                    className="border-b border-slate-100 px-3 py-1.5 align-top"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Renders an AI answer with markdown tables and Copy for Excel. */
export function AiAnswer({ answer }) {
  const segments = useMemo(() => parseAiAnswerSegments(answer), [answer])

  if (!answer) return null

  if (!segments.length) {
    return (
      <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-brand">
        {answer}
      </div>
    )
  }

  return (
    <div className="mt-3 space-y-2 text-sm leading-relaxed text-brand">
      {segments.map((segment, index) => {
        if (segment.type === 'table') {
          return <AiAnswerTable key={`table-${index}`} rows={segment.rows} />
        }
        return (
          <div key={`prose-${index}`} className="whitespace-pre-wrap">
            {segment.text}
          </div>
        )
      })}
    </div>
  )
}
